import { listCourses } from "./courseHelpers"
import { listOrgEnrollments } from "./enrollmentHelpers"
import { listOrgCertificates } from "./certHelpers"
import { listOrgOrders } from "./orderHelpers"
import { listOrgLearningEvents } from "./learningEventHelpers"
import { getCountriesForUsers } from "./learnerProfileHelpers"
import { getRangeBounds, toMillis, isWithin, percentChange } from "./dateRangeHelpers"

// Computes every KPI-row number from real Firestore data — courses, enrollments,
// certificates, orders — filtered against the selected date range, with a
// previous-period comparison for each. Fetches full collections client-side and
// filters in memory; simplest correct approach at current data volume — revisit
// with aggregation queries once an org's enrollment count gets large.
export const computeDashboardKPIs = async (orgId, rangeKey, customRange) => {
  const { start, end, prevStart, prevEnd } = getRangeBounds(rangeKey, customRange)

  const [{ courses }, { enrollments }, { certificates }, { orders }] = await Promise.all([
    listCourses(orgId),
    listOrgEnrollments(orgId),
    listOrgCertificates(orgId),
    listOrgOrders(orgId),
  ])

  // Learners: distinct userIds, cumulative as of each period boundary
  const learnersAsOf = (boundary) => {
    const ids = new Set()
    enrollments?.forEach((e) => {
      const ms = toMillis(e?.enrolledAt)
      if (ms !== null && ms <= boundary.getTime()) ids.add(e?.userId)
    })
    return ids
  }
  const totalLearnersNow = learnersAsOf(end).size
  const totalLearnersPrev = learnersAsOf(prevEnd).size

  // Active learners: distinct userIds with activity inside the current window
  const activeIds = new Set()
  enrollments?.forEach((e) => {
    if (isWithin(e?.lastActivityAt, start, end)) activeIds.add(e?.userId)
  })
  const activeLearners = activeIds.size

  // Products (= courses for now)
  const totalProducts = courses?.length || 0
  const publishedProducts = courses?.filter((c) => c?.status === "published")?.length || 0

  // Completion rate: % of enrollments (existing as of boundary) at 100%
  const completionRateAsOf = (boundary) => {
    const relevant = enrollments?.filter((e) => {
      const ms = toMillis(e?.enrolledAt)
      return ms !== null && ms <= boundary.getTime()
    })
    if (!relevant?.length) return 0
    const completed = relevant?.filter((e) => (e?.progressPercent || 0) >= 100)?.length
    return Math.round((completed / relevant.length) * 1000) / 10
  }
  const completionRateNow = completionRateAsOf(end)
  const completionRatePrev = completionRateAsOf(prevEnd)

  // Credentials issued within each period
  const credentialsNow = certificates?.filter((c) => isWithin(c?.issueDate, start, end))?.length || 0
  const credentialsPrev = certificates?.filter((c) => isWithin(c?.issueDate, prevStart, prevEnd))?.length || 0

  // Revenue: sum of paid orders within each period
  const paidOrders = orders?.filter((o) => o?.status === "paid") || []
  const revenueNow = paidOrders
    ?.filter((o) => isWithin(o?.paidAt || o?.createdAt, start, end))
    ?.reduce((sum, o) => sum + (o?.amount || 0), 0)
  const revenuePrev = paidOrders
    ?.filter((o) => isWithin(o?.paidAt || o?.createdAt, prevStart, prevEnd))
    ?.reduce((sum, o) => sum + (o?.amount || 0), 0)

  return {
    range: { start, end, prevStart, prevEnd },
    learners: { total: totalLearnersNow, change: percentChange(totalLearnersNow, totalLearnersPrev) },
    activeLearners: {
      total: activeLearners,
      pctOfTotal: totalLearnersNow > 0 ? Math.round((activeLearners / totalLearnersNow) * 1000) / 10 : 0,
    },
    products: { total: totalProducts, published: publishedProducts },
    revenue: {
      total: revenueNow || 0,
      change: percentChange(revenueNow || 0, revenuePrev || 0),
      hasAnyOrders: (orders?.length || 0) > 0,
    },
    completionRate: { value: completionRateNow, change: percentChange(completionRateNow, completionRatePrev) },
    credentials: { total: credentialsNow, change: percentChange(credentialsNow, credentialsPrev) },
  }
}

// --- Revenue Overview (Commerce > Revenue) -------------------------------
// All three draw from the same listOrgOrders() call the rest of the
// dashboard already uses — no new collections, just different aggregations
// over the same orders data.

const monthKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`
const monthLabel = (date) => date.toLocaleDateString("en-US", { month: "short" })

// Gross/Net revenue, paid customers, and AOV for the selected range, with a
// previous-period comparison, mirroring computeDashboardKPIs's shape.
// Note: "Net Revenue" = paid orders minus refunded orders in the same
// window — there's no separate fee/discount field on the order doc yet, so
// this is the closest real number available. Revisit if fees get tracked.
export const computeRevenueKPIs = async (orgId, rangeKey, customRange) => {
  const { start, end, prevStart, prevEnd } = getRangeBounds(rangeKey, customRange)
  const { orders } = await listOrgOrders(orgId)

  const paidInRange = (rangeStart, rangeEnd) =>
    orders?.filter((o) => o?.status === "paid" && isWithin(o?.paidAt || o?.createdAt, rangeStart, rangeEnd)) || []
  const refundedInRange = (rangeStart, rangeEnd) =>
    orders?.filter((o) => o?.status === "refunded" && isWithin(o?.paidAt || o?.createdAt, rangeStart, rangeEnd)) || []

  const grossNow = paidInRange(start, end).reduce((sum, o) => sum + (o?.amount || 0), 0)
  const grossPrev = paidInRange(prevStart, prevEnd).reduce((sum, o) => sum + (o?.amount || 0), 0)
  const refundsNow = refundedInRange(start, end).reduce((sum, o) => sum + (o?.amount || 0), 0)
  const refundsPrev = refundedInRange(prevStart, prevEnd).reduce((sum, o) => sum + (o?.amount || 0), 0)

  const netNow = grossNow - refundsNow
  const netPrev = grossPrev - refundsPrev

  const paidOrdersNow = paidInRange(start, end)
  const paidOrdersPrev = paidInRange(prevStart, prevEnd)

  const customersNow = new Set(paidOrdersNow?.map((o) => o?.customerEmail)?.filter(Boolean)).size
  const customersPrev = new Set(paidOrdersPrev?.map((o) => o?.customerEmail)?.filter(Boolean)).size

  const aovNow = paidOrdersNow?.length ? grossNow / paidOrdersNow.length : 0
  const aovPrev = paidOrdersPrev?.length ? grossPrev / paidOrdersPrev.length : 0

  return {
    grossRevenue: { total: grossNow, change: percentChange(grossNow, grossPrev) },
    netRevenue: { total: netNow, change: percentChange(netNow, netPrev) },
    paidCustomers: { total: customersNow, change: percentChange(customersNow, customersPrev) },
    averageOrderValue: { total: aovNow, change: percentChange(aovNow, aovPrev) },
    hasAnyOrders: (orders?.length || 0) > 0,
  }
}

// Trailing 12 months of paid revenue for the Revenue Trend chart. Always
// trailing-12, independent of the dashboard's date-range filter — a shorter
// monthly trend isn't very readable.
export const getMonthlyRevenue = async (orgId) => {
  const { orders } = await listOrgOrders(orgId)
  const paid = orders?.filter((o) => o?.status === "paid") || []

  const months = []
  const now = new Date()
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    months.push({ key: monthKey(d), label: monthLabel(d), revenue: 0 })
  }
  const byKey = Object.fromEntries(months.map((m) => [m.key, m]))

  paid?.forEach((o) => {
    const ms = toMillis(o?.paidAt || o?.createdAt)
    if (ms === null) return
    const key = monthKey(new Date(ms))
    if (byKey[key]) byKey[key].revenue += o?.amount || 0
  })

  return months
}

// Revenue grouped by product for the selected range: top 3 named
// individually, everything else rolled into "Other".
export const computeRevenueByProduct = async (orgId, rangeKey, customRange) => {
  const { start, end } = getRangeBounds(rangeKey, customRange)
  const { orders } = await listOrgOrders(orgId)
  const paid = orders?.filter((o) => o?.status === "paid" && isWithin(o?.paidAt || o?.createdAt, start, end)) || []

  const byProduct = {}
  paid?.forEach((o) => {
    const name = o?.productTitle || "Untitled Product"
    byProduct[name] = (byProduct[name] || 0) + (o?.amount || 0)
  })

  const sorted = Object.entries(byProduct)?.sort((a, b) => b[1] - a[1])
  const top = sorted?.slice(0, 3)
  const rest = sorted?.slice(3)
  const otherTotal = rest?.reduce((sum, [, value]) => sum + value, 0)

  const palette = ["hsl(214.7 95% 40%)", "hsl(142.1 76.2% 36.3%)", "hsl(47.9 95.8% 53.1%)", "hsl(0 0% 63.9%)"]
  const result = top?.map(([label, value], i) => ({ label, value, color: palette[i] }))
  if (otherTotal > 0) result.push({ label: "Other", value: otherTotal, color: palette[3] })

  return result?.length ? result : [{ label: "No revenue yet", value: 1, color: "hsl(0 0% 85%)" }]
}
// --- Customer Overview (Commerce > Customers) -----------------------------
// Customers are derived from paid orders (distinct customerEmail) — same
// approach documented in orderHelpers.js, no dedicated customers collection.
// Deliberately separate from "Learners" (computed elsewhere from
// enrollments): a customer is whoever paid; a learner is whoever's
// enrolled. A single B2B org customer can represent many learners who never
// touch a payment themselves — there's no headcount field on org orders yet
// to quantify that, so it isn't computed here, only called out as context.

const buildCustomerProfiles = (orders) => {
  const profiles = {}
  orders?.forEach((o) => {
    if (o?.status !== "paid" || !o?.customerEmail) return
    const email = o.customerEmail
    if (!profiles[email]) {
      profiles[email] = { email, type: o.customerType || "individual", firstPurchaseMs: null, orderCount: 0, totalSpent: 0 }
    }
    const ms = toMillis(o?.paidAt || o?.createdAt)
    if (ms !== null && (profiles[email].firstPurchaseMs === null || ms < profiles[email].firstPurchaseMs)) {
      profiles[email].firstPurchaseMs = ms
    }
    profiles[email].orderCount += 1
    profiles[email].totalSpent += o?.amount || 0
  })
  return Object.values(profiles)
}

export const computeCustomerKPIs = async (orgId, rangeKey, customRange) => {
  const { start, end, prevStart, prevEnd } = getRangeBounds(rangeKey, customRange)
  const { orders } = await listOrgOrders(orgId)
  const profiles = buildCustomerProfiles(orders)

  const asOf = (boundary) => profiles?.filter((p) => p.firstPurchaseMs !== null && p.firstPurchaseMs <= boundary.getTime())
  const totalNow = asOf(end)?.length || 0
  const totalPrev = asOf(prevEnd)?.length || 0

  const newInRange = (rangeStart, rangeEnd) =>
    profiles?.filter((p) => p.firstPurchaseMs !== null && p.firstPurchaseMs >= rangeStart.getTime() && p.firstPurchaseMs <= rangeEnd.getTime())
  const newNow = newInRange(start, end)?.length || 0
  const newPrev = newInRange(prevStart, prevEnd)?.length || 0

  const activeEmailsInRange = (rangeStart, rangeEnd) => {
    const emails = new Set()
    orders?.forEach((o) => {
      if (o?.status === "paid" && o?.customerEmail && isWithin(o?.paidAt || o?.createdAt, rangeStart, rangeEnd)) emails.add(o.customerEmail)
    })
    return emails
  }
  const activeNow = activeEmailsInRange(start, end)
  const activePrev = activeEmailsInRange(prevStart, prevEnd)

  const returningNow = [...activeNow]?.filter((email) => {
    const p = profiles?.find((pr) => pr.email === email)
    return p && p.firstPurchaseMs !== null && p.firstPurchaseMs < start.getTime()
  })?.length || 0
  const returningPrev = [...activePrev]?.filter((email) => {
    const p = profiles?.find((pr) => pr.email === email)
    return p && p.firstPurchaseMs !== null && p.firstPurchaseMs < prevStart.getTime()
  })?.length || 0

  const orgNow = asOf(end)?.filter((p) => p.type === "organization")?.length || 0
  const orgPrev = asOf(prevEnd)?.filter((p) => p.type === "organization")?.length || 0
  const indivNow = asOf(end)?.filter((p) => p.type === "individual")?.length || 0
  const indivPrev = asOf(prevEnd)?.filter((p) => p.type === "individual")?.length || 0

  return {
    totalCustomers: { total: totalNow, change: percentChange(totalNow, totalPrev) },
    newCustomers: { total: newNow, change: percentChange(newNow, newPrev) },
    returningCustomers: { total: returningNow, change: percentChange(returningNow, returningPrev) },
    organizations: { total: orgNow, change: percentChange(orgNow, orgPrev) },
    individuals: { total: indivNow, change: percentChange(indivNow, indivPrev) },
    hasAnyCustomers: profiles?.length > 0,
  }
}

// Average Spend is scoped to the selected range (recent spend behavior);
// Lifetime Value, Repeat Purchase Rate are intentionally all-time — a 7/30
// day window makes those numbers meaningless. Retention compares the
// previous period's purchasers against the current period.
export const computeCustomerValueMetrics = async (orgId, rangeKey, customRange) => {
  const { start, end, prevStart, prevEnd } = getRangeBounds(rangeKey, customRange)
  const { orders } = await listOrgOrders(orgId)
  const profiles = buildCustomerProfiles(orders)

  const totalCustomers = profiles?.length || 0
  const totalRevenueAllTime = profiles?.reduce((sum, p) => sum + p.totalSpent, 0) || 0
  const lifetimeValue = totalCustomers > 0 ? totalRevenueAllTime / totalCustomers : 0

  const revenueInRange = orders
    ?.filter((o) => o?.status === "paid" && isWithin(o?.paidAt || o?.createdAt, start, end))
    ?.reduce((sum, o) => sum + (o?.amount || 0), 0) || 0
  const activeCustomersInRange = new Set(
    orders?.filter((o) => o?.status === "paid" && o?.customerEmail && isWithin(o?.paidAt || o?.createdAt, start, end))?.map((o) => o.customerEmail)
  ).size
  const averageSpend = activeCustomersInRange > 0 ? revenueInRange / activeCustomersInRange : 0

  const repeatCustomers = profiles?.filter((p) => p.orderCount > 1)?.length || 0
  const repeatPurchaseRate = totalCustomers > 0 ? Math.round((repeatCustomers / totalCustomers) * 1000) / 10 : 0

  const emailsInWindow = (rangeStart, rangeEnd) => {
    const emails = new Set()
    orders?.forEach((o) => {
      if (o?.status === "paid" && o?.customerEmail && isWithin(o?.paidAt || o?.createdAt, rangeStart, rangeEnd)) emails.add(o.customerEmail)
    })
    return emails
  }
  const prevWindowEmails = emailsInWindow(prevStart, prevEnd)
  const currentWindowEmails = emailsInWindow(start, end)
  const retainedCount = [...prevWindowEmails]?.filter((email) => currentWindowEmails.has(email))?.length || 0
  const retentionRate = prevWindowEmails.size > 0 ? Math.round((retainedCount / prevWindowEmails.size) * 1000) / 10 : 0

  return { averageSpend, lifetimeValue, repeatPurchaseRate, retentionRate, hasAnyCustomers: totalCustomers > 0 }
}

// --- Intelligence Overview (Analytics tab) ---------------------------------
// All five functions below reuse listCourses()/listOrgEnrollments() — the
// same two calls computeDashboardKPIs already makes — just grouped
// differently per widget. No new collections, no new reads beyond what the
// dashboard already fetches.

// Cumulative distinct learners as of each month-end, trailing 12 months —
// a growth curve rather than per-month totals, since "total learners" is
// inherently a running count. Powers LearnersChart.
export const getLearnerTrend = async (orgId) => {
  const { enrollments } = await listOrgEnrollments(orgId)

  const months = []
  const now = new Date()
  for (let i = 11; i >= 0; i--) {
    const boundary = new Date(now.getFullYear(), now.getMonth() - i + 1, 0, 23, 59, 59)
    months.push({ boundary, month: monthLabel(boundary) })
  }

  const data = months.map(({ boundary, month }) => {
    const ids = new Set()
    enrollments?.forEach((e) => {
      const ms = toMillis(e?.enrolledAt)
      if (ms !== null && ms <= boundary.getTime()) ids.add(e?.userId)
    })
    return { month, learners: ids.size }
  })

  const total = data?.[data.length - 1]?.learners || 0
  const prevMonthTotal = data?.[data.length - 2]?.learners || 0

  return { data, total, deltaPercent: percentChange(total, prevMonthTotal) }
}

// Top courses by enrollment count, with real completion rate. Powers TopCourses.
export const getTopCourses = async (orgId, limitCount = 5) => {
  const [{ courses }, { enrollments }] = await Promise.all([listCourses(orgId), listOrgEnrollments(orgId)])

  const byCourse = {}
  enrollments?.forEach((e) => {
    if (!e?.courseId) return
    if (!byCourse[e.courseId]) byCourse[e.courseId] = { enrollments: 0, completed: 0 }
    byCourse[e.courseId].enrollments += 1
    if ((e?.progressPercent || 0) >= 100) byCourse[e.courseId].completed += 1
  })

  return Object.entries(byCourse)
    ?.map(([courseId, stats]) => {
      const course = courses?.find((c) => c?.courseId === courseId)
      return {
        title: course?.title || "Untitled Course",
        enrollments: stats.enrollments,
        completionRate: stats.enrollments > 0 ? Math.round((stats.completed / stats.enrollments) * 100) : 0,
      }
    })
    ?.sort((a, b) => b.enrollments - a.enrollments)
    ?.slice(0, limitCount)
}

// Enrollment counts grouped by course category. Powers EnrollmentByCategory.
export const getEnrollmentByCategory = async (orgId) => {
  const [{ courses }, { enrollments }] = await Promise.all([listCourses(orgId), listOrgEnrollments(orgId)])
  const categoryByCourseId = Object.fromEntries(courses?.map((c) => [c.courseId, c?.category || "Other"]) || [])

  const byCategory = {}
  enrollments?.forEach((e) => {
    const category = categoryByCourseId[e?.courseId] || "Other"
    byCategory[category] = (byCategory[category] || 0) + 1
  })

  return Object.entries(byCategory)
    ?.map(([label, count]) => ({ label, enrollments: count }))
    ?.sort((a, b) => b.enrollments - a.enrollments)
}

// Learner mix by engagement stage — one bucket per distinct learner (not
// per enrollment): "completed" if any enrollment is done, else
// active/inactive based on the most recent activity across all their
// enrollments. Powers LearnerMix.
export const getLearnerMix = async (orgId) => {
  const { enrollments } = await listOrgEnrollments(orgId)

  const byUser = {}
  enrollments?.forEach((e) => {
    if (!e?.userId) return
    if (!byUser[e.userId]) byUser[e.userId] = { completed: false, lastActivityMs: 0 }
    if ((e?.progressPercent || 0) >= 100) byUser[e.userId].completed = true
    const ms = toMillis(e?.lastActivityAt)
    if (ms !== null && ms > byUser[e.userId].lastActivityMs) byUser[e.userId].lastActivityMs = ms
  })

  const now = Date.now()
  const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000
  let completed = 0, active = 0, inactive = 0
  Object.values(byUser).forEach((u) => {
    if (u.completed) completed += 1
    else if (now - u.lastActivityMs <= thirtyDaysMs) active += 1
    else inactive += 1
  })

  const total = completed + active + inactive
  if (total === 0) return []

  const pct = (n) => Math.round((n / total) * 100)
  return [
    { label: "Actively enrolled", share: pct(active) },
    { label: "Completed a course", share: pct(completed) },
    { label: "Inactive 30+ days", share: pct(inactive) },
  ]
}

// "Active now" has no session/presence system to draw on yet, so this is a
// documented proxy: learners with activity on any enrollment in the last
// `windowMinutes`. Breakdown groups by the enrolled course's courseType.
// Powers EnrolledNow.
export const getActiveNow = async (orgId, windowMinutes = 30) => {
  const [{ courses }, { enrollments }] = await Promise.all([listCourses(orgId), listOrgEnrollments(orgId)])
  const typeByCourseId = Object.fromEntries(courses?.map((c) => [c.courseId, c?.courseType || "course"]) || [])

  const cutoff = Date.now() - windowMinutes * 60 * 1000
  const activeUserIds = new Set()
  const byType = {}

  enrollments?.forEach((e) => {
    const ms = toMillis(e?.lastActivityAt)
    if (ms === null || ms < cutoff || !e?.userId || activeUserIds.has(e.userId)) return
    activeUserIds.add(e.userId)
    const type = typeByCourseId[e?.courseId] || "course"
    byType[type] = (byType[type] || 0) + 1
  })

  const count = activeUserIds.size
  const breakdown = Object.entries(byType)
    ?.map(([label, n]) => ({ label, share: count > 0 ? Math.round((n / count) * 100) : 0 }))
    ?.sort((a, b) => b.share - a.share)

  return { count, breakdown }
}
// --- Intelligence → Learners -----------------------------------------------
// Activation, engagement, retention, completion, dropout, and assessment
// performance — all derived from the same enrollments/learningEvents this
// file already reads for the dashboard KPIs and Intelligence Overview.
// "Learning time" is deliberately not computed here: there's no session
// duration field on learningSessions yet, so a number would be fabricated
// rather than real — surfaced as "not available" in the UI instead.

export const computeLearnerIntelligence = async (orgId, rangeKey, customRange) => {
  const { start, end, prevStart, prevEnd } = getRangeBounds(rangeKey, customRange)
  const [{ enrollments }, { events }] = await Promise.all([
    listOrgEnrollments(orgId),
    listOrgLearningEvents(orgId),
  ])

  // Activation: of learners who FIRST enrolled inside a window, what % went
  // on to complete at least one lesson. A learner who enrolls but never
  // starts anything never activates — that gap is the point of this metric.
  const firstEnrollByUser = {}
  enrollments?.forEach((e) => {
    const ms = toMillis(e?.enrolledAt)
    if (ms === null || !e?.userId) return
    if (!firstEnrollByUser[e.userId] || ms < firstEnrollByUser[e.userId]) firstEnrollByUser[e.userId] = ms
  })
  const newInWindow = (rangeStart, rangeEnd) =>
    Object.entries(firstEnrollByUser)
      .filter(([, ms]) => ms >= rangeStart.getTime() && ms <= rangeEnd.getTime())
      .map(([uid]) => uid)

  const firstLessonByUser = {}
  events
    ?.filter((e) => e?.type === "lesson_completed")
    ?.forEach((e) => {
      const ms = toMillis(e?.createdAt)
      if (ms === null || !e?.userId) return
      if (!firstLessonByUser[e.userId] || ms < firstLessonByUser[e.userId]) firstLessonByUser[e.userId] = ms
    })

  const activationRate = (rangeStart, rangeEnd) => {
    const cohort = newInWindow(rangeStart, rangeEnd)
    if (!cohort.length) return 0
    const activated = cohort.filter((uid) => firstLessonByUser[uid] !== undefined).length
    return Math.round((activated / cohort.length) * 1000) / 10
  }
  const activationNow = activationRate(start, end)
  const activationPrev = activationRate(prevStart, prevEnd)

  // Engagement: distinct learners active inside the window, as a % of every
  // learner who existed as of the window's end.
  const learnersAsOf = (boundary) => {
    const ids = new Set()
    enrollments?.forEach((e) => {
      const ms = toMillis(e?.enrolledAt)
      if (ms !== null && ms <= boundary.getTime()) ids.add(e?.userId)
    })
    return ids
  }
  const activeIdsInWindow = (rangeStart, rangeEnd) => {
    const ids = new Set()
    enrollments?.forEach((e) => {
      if (isWithin(e?.lastActivityAt, rangeStart, rangeEnd)) ids.add(e?.userId)
    })
    return ids
  }
  const totalLearnersNow = learnersAsOf(end).size
  const activeNowIds = activeIdsInWindow(start, end)
  const activePrevIds = activeIdsInWindow(prevStart, prevEnd)
  const engagementRate = totalLearnersNow > 0 ? Math.round((activeNowIds.size / totalLearnersNow) * 1000) / 10 : 0

  // Retention: of learners active in the PREVIOUS period, what % came back
  // active in the current one. Same shape as computeCustomerValueMetrics'
  // retention, applied to learning activity instead of purchases.
  const retained = [...activePrevIds].filter((uid) => activeNowIds.has(uid)).length
  const retentionRate = activePrevIds.size > 0 ? Math.round((retained / activePrevIds.size) * 1000) / 10 : 0

  // Completion / dropout, scoped to enrollments that existed as of window end.
  const relevantEnrollments =
    enrollments?.filter((e) => {
      const ms = toMillis(e?.enrolledAt)
      return ms !== null && ms <= end.getTime()
    }) || []

  const completedCount = relevantEnrollments.filter((e) => (e?.progressPercent || 0) >= 100).length
  const completionRate =
    relevantEnrollments.length > 0 ? Math.round((completedCount / relevantEnrollments.length) * 1000) / 10 : 0

  // Dropout: started (>0%), not completed, inactive 30+ days. Per-enrollment
  // rather than per-learner, since one learner can drop course A while
  // still finishing course B.
  const staleMs = 30 * 24 * 60 * 60 * 1000
  const now = Date.now()
  const droppedCount = relevantEnrollments.filter((e) => {
    const progress = e?.progressPercent || 0
    const lastMs = toMillis(e?.lastActivityAt)
    return progress > 0 && progress < 100 && (lastMs === null || now - lastMs > staleMs)
  }).length
  const dropoutRate =
    relevantEnrollments.length > 0 ? Math.round((droppedCount / relevantEnrollments.length) * 1000) / 10 : 0

  // Assessment performance — same moduleQuizResults/capstoneResult shape as
  // the Proof of Learning evidence chain, aggregated org-wide instead of
  // per-learner. avgScore/passRate are null (not 0) when there's no graded
  // data yet, so the UI can show "No data" instead of a misleading zero.
  let totalAttempts = 0
  let scoreSum = 0
  let scoreCount = 0
  let passedCount = 0
  let gradedCount = 0

  relevantEnrollments.forEach((e) => {
    const results = [
      ...Object.values(e?.moduleQuizResults || {}),
      ...(e?.capstoneResult?.attemptCount ? [e.capstoneResult] : []),
    ]
    results.forEach((r) => {
      if (!r?.attemptCount) return
      totalAttempts += r.attemptCount
      const scoreValue = typeof r?.bestScore === "number" ? r.bestScore : r?.score
      if (typeof scoreValue === "number") {
        scoreSum += scoreValue
        scoreCount += 1
      }
      if (typeof r?.passed === "boolean") {
        gradedCount += 1
        if (r.passed) passedCount += 1
      }
    })
  })

  return {
    activation: { rate: activationNow, change: percentChange(activationNow, activationPrev) },
    engagement: {
      rate: engagementRate,
      activeCount: activeNowIds.size,
      change: percentChange(activeNowIds.size, activePrevIds.size),
    },
    retention: { rate: retentionRate },
    completion: { rate: completionRate, count: completedCount, total: relevantEnrollments.length },
    dropout: { rate: dropoutRate, count: droppedCount },
    assessmentPerformance: {
      avgScore: scoreCount > 0 ? Math.round(scoreSum / scoreCount) : null,
      passRate: gradedCount > 0 ? Math.round((passedCount / gradedCount) * 1000) / 10 : null,
      totalAttempts,
    },
  }
}

// Real learner-by-country breakdown, cumulative as-of the range boundary
// (same convention as computeDashboardKPIs' learner totals) rather than
// "new in window" — country distribution is a snapshot, not a flow metric.
export const getLearnersByCountry = async (orgId, rangeKey = "12m") => {
  const { enrollments } = await listOrgEnrollments(orgId)
  const { start, end, prevStart, prevEnd } = getRangeBounds(rangeKey)

  const uids = [...new Set(enrollments?.map((e) => e?.userId).filter(Boolean))]
  const countryByUid = await getCountriesForUsers(uids)

  const countAsOf = (boundary) => {
    const counts = {}
    const seen = new Set()
    enrollments?.forEach((e) => {
      if (!e?.userId || seen.has(e.userId)) return
      const country = countryByUid[e.userId]
      if (!country) return
      const ms = toMillis(e?.enrolledAt)
      if (ms === null || ms > boundary.getTime()) return
      counts[country] = (counts[country] || 0) + 1
      seen.add(e.userId)
    })
    return counts
  }

  const currentCounts = countAsOf(end)
  const prevCounts = countAsOf(prevEnd)

  const rows = Object.entries(currentCounts)
    .map(([code, learners]) => ({
      code,
      learners,
      delta: percentChange(learners, prevCounts[code] || 0) ?? 0,
    }))
    .sort((a, b) => b.learners - a.learners)
    .slice(0, 10)

  return { rows, hasData: Object.keys(countryByUid).length > 0 }
}